import { useEffect, useRef } from "react";

/**
 * Hook para actualizar los meta tags de la página en runtime.
 * Actualiza <title>, <meta name="description">, Open Graph, Twitter Cards
 * y <link rel="canonical">.
 *
 * Al ser SPA (Create React App sin SSR) es la mejor opción hasta migrar a Next.
 *
 * Uso:
 *   useSEO({
 *     title: "Sobre nosotros",
 *     description: "Conocé nuestra historia y equipo.",
 *     path: "/sobre-nosotros" // opcional; si no se pasa se toma window.location.pathname
 *   });
 */

const SITE_URL = "https://www.seguroweb.com.ar";
const DEFAULT_OG_IMAGE = `${SITE_URL}/logos/gys/seguroweb.svg`;

// Helpers para leer/crear tags sin romper si no existen ---------------------
const getOrCreateMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
};

const getOrCreateCanonical = () => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  return el;
};

export const useSEO = ({ title, description, path, image } = {}) => {
  // Guardamos los valores originales para restaurarlos al desmontar
  const prevTitle = useRef(document.title);
  const prevDescription = useRef(
    document.querySelector('meta[name="description"]')?.getAttribute("content") || ""
  );
  const prevCanonical = useRef(
    document.querySelector('link[rel="canonical"]')?.getAttribute("href") || SITE_URL
  );

  useEffect(() => {
    const previousTitle = prevTitle.current;
    const finalTitle = title ? `${title} | SeguroWeb` : previousTitle;

    if (title) {
      document.title = finalTitle;
    }

    // Description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (description && metaDescription) {
      metaDescription.setAttribute("content", description);
    }

    // Canonical
    const canonicalUrl = `${SITE_URL}${path || window.location.pathname}`.replace(/\/$/, "") || SITE_URL;
    const canonical = getOrCreateCanonical();
    canonical.setAttribute("href", canonicalUrl || SITE_URL);

    // Open Graph
    const ogTitle = getOrCreateMeta('meta[property="og:title"]', { property: "og:title" });
    const ogDescription = getOrCreateMeta('meta[property="og:description"]', { property: "og:description" });
    const ogUrl = getOrCreateMeta('meta[property="og:url"]', { property: "og:url" });
    const ogImage = getOrCreateMeta('meta[property="og:image"]', { property: "og:image" });

    ogTitle.setAttribute("content", finalTitle);
    if (description) ogDescription.setAttribute("content", description);
    ogUrl.setAttribute("content", canonicalUrl || SITE_URL);
    ogImage.setAttribute("content", image || DEFAULT_OG_IMAGE);

    // Twitter
    const twTitle = getOrCreateMeta('meta[name="twitter:title"]', { name: "twitter:title" });
    const twDescription = getOrCreateMeta('meta[name="twitter:description"]', { name: "twitter:description" });
    const twUrl = getOrCreateMeta('meta[name="twitter:url"]', { name: "twitter:url" });
    const twImage = getOrCreateMeta('meta[name="twitter:image"]', { name: "twitter:image" });

    twTitle.setAttribute("content", finalTitle);
    if (description) twDescription.setAttribute("content", description);
    twUrl.setAttribute("content", canonicalUrl || SITE_URL);
    twImage.setAttribute("content", image || DEFAULT_OG_IMAGE);

    return () => {
      // Restauramos valores previos al desmontar
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute("content", prevDescription.current);
      }
      canonical.setAttribute("href", prevCanonical.current);
    };
  }, [title, description, path, image]);
};

export default useSEO;
